
import { Restore, Save } from "@mui/icons-material";
import { Button, Divider, Paper, TextField, Typography } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useSocket } from "../../context/useSocket";
import { useDispatch } from "react-redux";
import { setPadData } from "../../reducers/rtfoSlice";
import { rtfoConversionFactors } from "./rtfoConversionFactors";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  },
  table: {
    overflow: 'auto',
    marginTop: 0,
    marginBottom: 0,
    border: 'none'
  },
  endAdornment: {
    fontSize: '12px'
  }
});

export const RTFOFrictionModelDataTable = ({ currentRTFO, frictionModelParams, setFrictionModelParams, setPpipeArray, PpipeCoefficients, darkMode }) => {

  
    // STYLES
    const classes = useStyles();
    const theme = useTheme();

    const dispatch = useDispatch();
    const { socket } = useSocket();

    const [Fs] = useState(1);
    const [nPrime, setNPrime] = useState(0.7);
    const [Fd, setFd] = useState(1);
    const [kPrime, setKPrime] = useState(0.000055);
    const [Ff, setFf] = useState(1);

    const units = currentRTFO?.settings?.units || 'metric';

    const rows = useMemo(() => {
      try {
        
        const stageData = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
        const preSandStepdownData = stageData?.preSandStepdownData || [];
        const stepdownData = stageData?.stepdownData || [];
        const stepdownParams = stageData?.stepdownParams;
        if (!stageData || Object.keys(stageData)?.length === 0 || (!stepdownData && !preSandStepdownData) || stepdownData?.length === 0 || !stepdownParams) return [];
        const stageISIP = stepdownParams?.isip;
        
        const _rows = [];

        if (preSandStepdownData?.length > 1) {
          preSandStepdownData?.forEach((step,i) => {
            _rows.push({
              id: `presand-${i}`,
              sdtType: 'Pre Sand',
              step: i+1,
              depth: Number((stepdownParams?.averageDepth * (units === 'field' ? rtfoConversionFactors.depth : 1))?.toFixed(1)) || 0, 
              rate: Number((step?.rate * (units === 'field' ? rtfoConversionFactors.rate : 1))?.toFixed(2)) || 0,
              pressure: Number((step?.pressure * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2)) || 0,
              isip: Number((stepdownParams?.preSandISIP * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2)) || 0,
              totalFriction: Number(((step?.pressure - stepdownParams?.preSandISIP) * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2)) || 0,
              frictionGradient: 
                units === 'field' ?
                Number((((step?.pressure - stepdownParams?.preSandISIP) / stepdownParams?.averageDepth)  * (units === 'field' ? rtfoConversionFactors.frictionGradient : 1))?.toFixed(2)) || 0 :
                Number((((step?.pressure - stepdownParams?.preSandISIP) * 1000 / stepdownParams?.averageDepth))?.toFixed(2)) || 0,
              fc: Number((stepdownParams?.preSandFCFrictionModel * (units === 'field' ? rtfoConversionFactors.fc(stepdownParams?.preSandPLExpFrictionModel) : 1))?.toFixed(7)) || 0,
              plExp: Number(stepdownParams?.preSandPLExpFrictionModel?.toFixed(4)) || 0
            });
          });
        }

        if (stepdownData?.length > 1) {
          stepdownData?.forEach((step,i) => {
            _rows.push({
              id: `flush-${i}`,
              sdtType: 'Flush',
              step: i+1,
              depth: Number((stepdownParams?.averageDepth * (units === 'field' ? rtfoConversionFactors.depth : 1))?.toFixed(1)) || 0, 
              rate: Number((step?.rate * (units === 'field' ? rtfoConversionFactors.rate : 1))?.toFixed(2)) || 0,
              pressure: Number((step?.pressure * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2)) || 0,
              isip: Number((stageISIP * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2)) || 0,
              totalFriction: Number(((step?.pressure - stageISIP) * (units === 'field' ? rtfoConversionFactors.depth : 1))?.toFixed(2)) || 0,
              frictionGradient: 
                units === 'field' ?
                Number((((step?.pressure - stageISIP) / stepdownParams?.averageDepth) * (units === 'field' ? rtfoConversionFactors.frictionGradient : 1))?.toFixed(2)) || 0 :
                Number(((step?.pressure - stageISIP) * 1000 / stepdownParams?.averageDepth)?.toFixed(2)),
              fc: Number((stepdownParams?.fcFrictionModel * (units === 'field' ? rtfoConversionFactors.fc(stepdownParams?.plExpFrictionModel) : 1))?.toFixed(7)) || 0,
              plExp: Number(stepdownParams?.plExpFrictionModel?.toFixed(4)) || 0
            });
          });
        };

        return _rows;
      } catch (error) {
        return [];
      };
    }, [currentRTFO?.currentStageName, currentRTFO?.stageData, units]);
  
    const wellName = currentRTFO?.currentStageName?.split('_')?.[1]?.toLowerCase();
    const stageNumber = currentRTFO?.currentStageName?.split('_')?.[2]?.replace(/[^0-9]+/g, '');
    const cluster = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
    const depths = cluster && Object.keys(cluster)?.map(d => cluster[d]?.shotDepth);

    const mdAvg = depths?.reduce((a, b) => a + b, 0) / depths?.length;
    const lengthCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.md > 0 ? currentRTFO?.settings?.wells?.[wellName]?.casing1?.md : mdAvg;
    const lengthCasing2 = mdAvg - lengthCasing1;
    const odCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.od || 0;
    const odCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.od || 0;
    const idCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.md > 0 ? currentRTFO?.settings?.wells?.[wellName]?.casing1?.id : (currentRTFO?.settings?.wells?.[wellName]?.casing1?.od || 0);
    const idCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.md > 0 ? currentRTFO?.settings?.wells?.[wellName]?.casing2?.id : (currentRTFO?.settings?.wells?.[wellName]?.casing2?.od || 0);

    const stageData = currentRTFO?.stageData?.[currentRTFO?.currentStageName];

    useEffect(() => {
      const PpipeArray = [];
      for (const step of stageData?.stepdownData) {
        const Q = step?.rate;
        const dP = calculatePipeFriction(stageData?.metrics, Fs, parseFloat(nPrime), Fd, kPrime, Ff, lengthCasing1, lengthCasing2, idCasing1, idCasing2, Q);
        PpipeArray.push(dP);
      };

      setPpipeArray(PpipeArray);
    }, [Fd, Ff, Fs, idCasing1, idCasing2, kPrime, lengthCasing1, lengthCasing2, nPrime, setPpipeArray, stageData?.metrics, stageData?.stepdownData]);


  try {

    const columns = [
      {
        field: 'sdtType',
        headerName: 'SDT Type',
        width: 75,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'step',
        headerName: 'Step',
        width: 50,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'depth',
        headerName: `Depth of Calc. (${units === 'field' ? 'ft' : 'm'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'rate',
        headerName: `Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pressure',
        headerName: `Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'isip',
        headerName: `ISIP (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'totalFriction',
        headerName: `Total Friction (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'frictionGradient',
        headerName: `Friction Grad. (${units === 'field' ? 'psi/ft' : 'kPa/m'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'fc',
        headerName: 'Coeff.',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'plExp',
        headerName: 'PL Exp.',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      }
    ];

    
    return (
      <Paper className={classes.root} elevation={0}>
        <div style={{display: 'flex', marginTop: 10, marginBottom: 10, alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography fontWeight={'bold'} style={{marginRight: 20, fontSize: '12px'}}>Manual Parameters</Typography>

          <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginRight: 10, marginBottom: 10}}>
              <div style={{display: 'flex', flexDirection: 'column', width: 125, color: lengthCasing1 === 0 && 'grey'}}>
                <Typography style={{fontSize: 14}}>Casing 1</Typography>
                <Typography variant="caption">{(lengthCasing1 * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(1)} {units === 'field' ? 'ft' : 'm'} | {(odCasing1 * (units === 'field' ? rtfoConversionFactors.casingDiameter : 1))?.toFixed(1)} {units === 'field' ? 'in' : 'mm'}</Typography>
              </div>

              {['fcCasing1', 'plExpCasing1']?.map(param => {
                return (
                  <div style={{display: 'flex', alignItems: 'center', marginRight: 30, color: lengthCasing1 === 0 && 'grey'}} key={param}>
                    <Typography style={{fontSize: '12px'}}>{param === 'fcCasing1' ? 'Coefficient' : 'Exponent'}:</Typography>
                    <TextField 
                      type="string" 
                      variant="outlined" 
                      value={frictionModelParams?.[param]} 
                      style={{width: 100, marginLeft: 10}} 
                      onChange={e => handleParamChange(param, e.target.value)}
                      InputProps={{style: {fontSize: '12px', height: '28px'}}}
                      onFocus={event => {
                        event.target.select();
                      }}
                      disabled={lengthCasing1 === 0}
                    />
                  </div>
                );
              })}

              <Button 
                variant="outlined" 
                startIcon={<Restore />} 
                size="small" 
                style={{
                  height: 28, 
                  width: 120, 
                  color: (darkMode ? '#fff' : theme.palette.primary.main), 
                  borderColor: (darkMode ? '#fff' : theme.palette.primary.main)
                }} 
                onClick={handleManualParamsReset}
              >
                Reset
              </Button>

            </div>

            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginRight: 10}}>
              <div style={{display: 'flex', flexDirection: 'column', width: 125, color: lengthCasing2 === 0 && 'grey'}}>
                <Typography style={{fontSize: 14}}>Casing 2</Typography>
                <Typography variant="caption">{(lengthCasing2 * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(1)} {units === 'field' ? 'ft' : 'm'} | {(odCasing2 * (units === 'field' ? rtfoConversionFactors.casingDiameter : 1))?.toFixed(1)} {units === 'field' ? 'in' : 'mm'}</Typography>
              </div>
              {['fcCasing2', 'plExpCasing2']?.map(param => {
                return (
                  <div style={{display: 'flex', alignItems: 'center', marginRight: 30, color: lengthCasing2 === 0 && 'grey'}} key={param}>
                    <Typography style={{fontSize: '12px'}}>{param === 'fcCasing2' ? 'Coefficient' : 'Exponent'}:</Typography>
                    <TextField 
                      type="string" 
                      variant="outlined" 
                      value={frictionModelParams?.[param]}
                      style={{width: 100, marginLeft: 10}} 
                      onChange={e => handleParamChange(param, e.target.value)}
                      InputProps={{style: {fontSize: '12px', height: '28px'}}}
                      onFocus={event => {
                        event.target.select();
                      }}
                      disabled={lengthCasing2 === 0}
                    />
                  </div>
                );
              })}

              <Button variant="contained" startIcon={<Save />} size="small" style={{height: 28, width: 120}} onClick={handleManualParamsSave} color="secondary">Overwrite</Button>
            </div>
          </div>
 
        </div>

        <Divider />

        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 20, marginBottom: 20}}>
          
          <Typography fontWeight={'bold'} style={{fontSize: '12px'}}>Pipe Friction</Typography>

          <div style={{display: 'flex', flexDirection: 'column', marginLeft: 20 }}>
            <div style={{display: 'flex', flexDirection: 'row'}}>
              <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: 100, marginLeft: 10}}>
                <Typography variant='caption'>n'</Typography>
                <TextField 
                  type="string" 
                  variant="outlined" 
                  value={nPrime} 
                  onChange={e => setNPrime(e.target.value)}
                  InputProps={{style: {fontSize: '12px', height: '28px'}}}
                  onFocus={event => {
                    event.target.select();
                  }}
                />
              </div>
              
              <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: 100, marginLeft: 10}}>
                <Typography variant='caption'>Fd</Typography>
                <TextField 
                  type="string" 
                  variant="outlined" 
                  value={Fd} 
                  onChange={e => setFd(e.target.value)}
                  InputProps={{style: {fontSize: '12px', height: '28px'}}}
                  onFocus={event => {
                    event.target.select();
                  }}
                />
              </div>

              <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: 100, marginLeft: 10}}>
                <Typography variant='caption'>k'</Typography>
                <TextField 
                  type="string" 
                  variant="outlined" 
                  value={kPrime} 
                  onChange={e => setKPrime(e.target.value)}
                  InputProps={{style: {fontSize: '12px', height: '28px'}}}
                  onFocus={event => {
                    event.target.select();
                  }}
                />
              </div>

              <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: 100, marginLeft: 10}}>
                <Typography variant='caption'>Ff</Typography>
                <TextField 
                  type="string" 
                  variant="outlined" 
                  value={Ff} 
                  onChange={e => setFf(e.target.value)}
                  InputProps={{style: {fontSize: '12px', height: '28px'}}}
                  onFocus={event => {
                    event.target.select();
                  }}
                />
              </div>
            </div>

            {PpipeCoefficients?.[0] > 0 && PpipeCoefficients?.[1] > 0 && <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 10, marginLeft: 10}}>
              <Typography style={{marginRight: 20}}>Pipe Friction Fit Equation</Typography>
              <Typography variant='h5'>FG<sub>pipe</sub> = {(PpipeCoefficients?.[0])?.toFixed(7)} * q<sup>{PpipeCoefficients?.[1]?.toFixed(4)}</sup></Typography>
            </div>}
          </div>
        </div>

        <Divider />

        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={10}
          rowsPerPageOptions={[100]}
          disableSelectionOnClick
          className={classes.table}
          components={{
            Toolbar: CustomToolbar,
          }}
          density="compact"
          style={{fontSize: '12px'}}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: darkMode ? '#263238' : 'whitesmoke',
            },
          }}
        />
      </Paper>
    );

    function CustomToolbar() {
      return (
        <GridToolbarContainer>
          <GridToolbarColumnsButton style={{ color: darkMode && 'lightgrey' }} />
          <GridToolbarFilterButton style={{ color: darkMode && 'lightgrey' }} />
          <GridToolbarDensitySelector style={{ color: darkMode && 'lightgrey' }} />
          <GridToolbarExport style={{ color: darkMode && 'lightgrey' }} />
        </GridToolbarContainer>
      );
    };


    function handleParamChange(param, value) {
      try {

        let newValue = value;

        if (value?.charAt(0) === '.') {
          newValue = '0.'.concat(value?.substring(1, ));
        };

        newValue && setFrictionModelParams({...frictionModelParams, [param]: newValue});
      } catch (error) {
        console.log(error);
      };
    };

    function handleManualParamsReset() {
      const stageData = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
      const stepdownParams = stageData?.stepdownParams;
      setFrictionModelParams({
        fcCasing1: (stepdownParams?.fcCasing1 || 0.033) * (units === 'field' ? rtfoConversionFactors.fc(stepdownParams?.plExpCasing1 || 1.7) : 1), 
        plExpCasing1: stepdownParams?.plExpCasing1 || 1.7, 
        fcCasing2: (stepdownParams?.fcCasing2 || 0.085) * (units === 'field' ? rtfoConversionFactors.fc(stepdownParams?.plExpCasing2 || 1.701) : 1), 
        plExpCasing2: stepdownParams?.plExpCasing2 || 1.701
      });
    };

    function handleManualParamsSave() {
      try {
        const stageData = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
        const stepdownParams = JSON.parse(JSON.stringify(stageData?.stepdownParams));

        // Update pad settings for all wells to use new friction model params
        const newSettings = JSON.parse(JSON.stringify(currentRTFO?.settings));

        for (const wellName of Object.keys(newSettings?.wells)) {
          newSettings.wells[wellName].casing1.fc = 
            parseFloat(frictionModelParams?.fcCasing1) / (units === 'field' ? rtfoConversionFactors.fc(parseFloat(frictionModelParams?.plExpCasing1)) : 1);
          newSettings.wells[wellName].casing1.plExp = parseFloat(frictionModelParams?.plExpCasing1);
          newSettings.wells[wellName].casing2.fc = 
            parseFloat(frictionModelParams?.fcCasing2) / (units === 'field' ? rtfoConversionFactors.fc(parseFloat(frictionModelParams?.plExpCasing2)) : 1);
          newSettings.wells[wellName].casing2.plExp = parseFloat(frictionModelParams?.plExpCasing2);
        };

        stepdownParams.fcCasing1 = frictionModelParams?.fcCasing1 / (units === 'field' ? rtfoConversionFactors.fc(parseFloat(frictionModelParams?.plExpCasing1)) : 1);
        stepdownParams.fcCasing2 = frictionModelParams?.fcCasing2 / (units === 'field' ? rtfoConversionFactors.fc(parseFloat(frictionModelParams?.plExpCasing2)) : 1);
        stepdownParams.plExpCasing1 = frictionModelParams?.plExpCasing1;
        stepdownParams.plExpCasing2 = frictionModelParams?.plExpCasing2;
        stepdownParams.lockedParams = [];

        let sdtParams = null;

        if (stepdownParams) {
          sdtParams = {};
          Object.keys(stepdownParams)?.forEach(p => {
            if ((p !== 'lockedParams' && stepdownParams?.lockedParams?.indexOf(p) !== -1) || p === 'fcCasing1' || p === 'fcCasing2' || p === 'plExpCasing1' || p === 'plExpCasing2' ) {
              if (p === 'preSandDperf' || p === 'Dperf') {
                sdtParams[p] = parseFloat(stepdownParams[p]) / (units === 'field' ? 1 : 25.4);
              } else {
                sdtParams[p] = parseFloat(stepdownParams[p]);
              };
            };
          });
        };

        // update local state
        dispatch(setPadData({...currentRTFO, settings: newSettings}));

        // update database
        if (socket && socket.readyState === 1) {
          socket?.send(JSON.stringify({
            messageType: 'recalculateStepdown',
            padName: currentRTFO?.name,
            stage: stageData,
            perfSchedule: currentRTFO?.perfSchedule,
            surveys: currentRTFO?.surveys,
            settings: newSettings,
            params: sdtParams,
            sdtType: 'flush',
            saveStage: true,
            updateSettings: true,
            showSnackbar: true
          }));
        };
      } catch (error) {
        console.log(error);
        return;
      };
    };

    

  } catch (error) {
    return <></>
  };

  function calculatePipeFriction(metrics, Fs, nPrime, Fd, kPrime, Ff, lengthCasing1, lengthCasing2, idCasing1, idCasing2, rate) {
    try {

      // Input parameters
      // const Cv = ((metrics?.totalSlurryVolume - metrics?.totalCleanVolume) / metrics?.totalSlurryVolume) || 0.03; // this value is always 0 in flush SDT cases
      const d1 = idCasing1 * 0.0393701; // inches
      const d2 = idCasing2 * 0.0393701; // inches
      const L1 = lengthCasing1 * 3.28084; // ft
      const L2 = lengthCasing2 * 3.28084; // ft
      const RhoF = 8.3 * 0.119826; // g/cm3
      const Q = rate * 6.2898; // barrels per minute

      // step 1: calculate Xs
      // const Xs = Math.pow((1 - Cv), (-1.8 * Fs));
      const Xs = 1; // this value is always 1 in flush SDT cases, change for other types of calculations
      
      // step 2: calculate Pl laminar pressure drop
      const Pl1 = (d1 > 0 ? (Math.pow(d1, -(3 * nPrime + 1)) * ((Fd * kPrime) / 3) * Math.pow(1647, nPrime) * Xs * Ff * RhoF) : 0);
      const Pl2 = (d2 > 0 ? (Math.pow(d2, -(3 * nPrime + 1)) * ((Fd * kPrime) / 3) * Math.pow(1647, nPrime) * Xs * Ff * RhoF) : 0);
      
      // step 3: calculate Ret
      const Ret = (4000 * nPrime) / (Math.pow((1 + 3 * nPrime), 2) * Math.pow((1 / (3 + nPrime)), ((3 + nPrime) / (1 + nPrime))));
      
      // step 4: calculate T tubulent flow factor
      const T1 = (d1 > 0 ? ((Math.pow(d1, 2) / 17.16) * Math.pow(((3 * 32.2 * Fd * kPrime * Xs * (Math.pow(8, nPrime - 1)) * Ret) / (62.4 * Math.pow(d1 / 12, nPrime))), (1 / (2 - nPrime)))) : 0);
      const T2 = (d2 > 0 ? ((Math.pow(d2, 2) / 17.16) * Math.pow(((3 * 32.2 * Fd * kPrime * Xs * (Math.pow(8, nPrime - 1)) * Ret) / (62.4 * Math.pow(d2 / 12, nPrime))), (1 / (2 - nPrime)))) : 0);
      
      // step 5: calculate dP pipe friction pressure drop
      let dP = 
        (L1 > 0 ? (L1 * Math.pow(Q, nPrime) * Pl1) / Math.pow((1 + Math.pow(Q / T1, 4)), -0.25) : 0) + 
        (L2 > 0 ? (L2 * Math.pow(Q, nPrime) * Pl2) / Math.pow((1 + Math.pow(Q / T2, 4)), -0.25) : 0);
      
      dP *= 6.89476; // kPa

      return dP;

    } catch(error) {
      console.log(error);
      return 0;
    };
  };
};

